import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/cn'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAthletes } from '@/hooks/useAthletes'
import { useAthlete } from '@/hooks/useAthlete'
import { useAthleteProgram } from '@/hooks/usePrograms'
import { useSaveSet, useExerciseHistory, useSessionSets, isRealAthleteId } from '@/hooks/useSetLogs'
import { useExerciseLibrary } from '@/hooks/useExerciseLibrary'
import { groupSupersets } from '@/lib/supersets'
import { parseWeight } from '@/lib/parseWeight'
import { useDayCompletions, useToggleDayComplete, dayKey } from '@/hooks/useDayCompletions'
import { useLastTestDate } from '@/hooks/useTestingResults'
import { testingDue } from '@/lib/testingAnalytics'
import { Logo } from '@/components/Logo'
import type { ProgramExercise } from '@/types'

/**
 * 4-quadrant gym view for the iPad. Assign an athlete to each quadrant; they
 * scroll their current session, tick exercises off, and log weights inline.
 */
const GYM_SLOTS_KEY = 'fz-gym-slots'

export default function GymView() {
  const navigate = useNavigate()
  // Quadrant athletes persist per device: the gym iPad keeps whoever is in it
  // across refreshes/redeploys so athletes don't have to re-enter their name.
  const [slots, setSlots] = useState<(string | null)[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(GYM_SLOTS_KEY) || 'null')
      if (Array.isArray(saved) && saved.length === 4) {
        return saved.map((v) => (typeof v === 'string' ? v : null))
      }
    } catch {
      /* ignore malformed/disabled storage */
    }
    return [null, null, null, null]
  })

  useEffect(() => {
    try {
      localStorage.setItem(GYM_SLOTS_KEY, JSON.stringify(slots))
    } catch {
      /* storage disabled — session-only */
    }
  }, [slots])

  const setSlot = (i: number, id: string | null) =>
    setSlots((prev) => prev.map((s, j) => (j === i ? id : s)))

  return (
    <div className="flex h-screen flex-col">
      <header className="flex flex-shrink-0 items-center gap-4 border-b-2 border-gold/60 bg-surface px-5 py-2.5">
        <Logo className="h-7" />
        <span className="font-condensed text-[11px] uppercase tracking-[0.3em] text-muted">Gym View</span>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => navigate('/coach')}>
          Exit
        </Button>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2">
        {slots.map((athleteId, i) => (
          <div
            key={i}
            className={cn(
              'min-h-0 overflow-hidden border-border',
              i % 2 === 0 && 'border-r',
              i < 2 && 'border-b'
            )}
          >
            {athleteId ? (
              <Quadrant athleteId={athleteId} onRemove={() => setSlot(i, null)} />
            ) : (
              <EmptySlot onPick={(id) => setSlot(i, id)} takenIds={slots.filter(Boolean) as string[]} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function EmptySlot({ onPick, takenIds }: { onPick: (id: string) => void; takenIds: string[] }) {
  const { data: athletes = [] } = useAthletes(true)
  const [query, setQuery] = useState('')
  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return athletes
      .filter((a) => a.name.toLowerCase().includes(q) && !takenIds.includes(a.id))
      .slice(0, 6)
  }, [query, athletes, takenIds])

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
      <p className="font-condensed text-sm uppercase tracking-[0.2em] text-subtle">Add athlete</p>
      <div className="w-full max-w-xs">
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" />
        {query && (
          <div className="mt-2 overflow-hidden rounded-lg border border-border">
            {results.length === 0 ? (
              <p className="px-3 py-2 text-xs italic text-muted">No athletes</p>
            ) : (
              results.map((a) => (
                <button
                  key={a.id}
                  onClick={() => onPick(a.id)}
                  className="block w-full border-b border-border px-3 py-2 text-left font-condensed font-bold last:border-0 hover:bg-surface-2"
                >
                  {a.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** "Day 1" / "day1" / "D1" → "Day 1"; otherwise falls back to the position. */
function dayLabel(label: string, i: number) {
  const m = /\d+/.exec(label ?? '')
  return `Day ${m ? m[0] : i + 1}`
}

type GymState = { dayIndex: number; ticks: Record<string, number[]> }
const today = () => new Date().toLocaleDateString('en-CA')
const gymStateKey = (athleteId: string) => `fz-gym-q-${athleteId}`

function loadGymState(athleteId: string): GymState {
  try {
    const s = JSON.parse(localStorage.getItem(gymStateKey(athleteId)) || 'null')
    if (s && s.date === today() && Number.isInteger(s.dayIndex) && s.ticks && typeof s.ticks === 'object') {
      return { dayIndex: s.dayIndex, ticks: s.ticks }
    }
  } catch {
    /* ignore malformed/disabled storage */
  }
  return { dayIndex: 0, ticks: {} }
}

function saveGymState(athleteId: string, state: GymState) {
  try {
    localStorage.setItem(gymStateKey(athleteId), JSON.stringify({ ...state, date: today() }))
  } catch {
    /* storage disabled — session-only */
  }
}

function Quadrant({ athleteId, onRemove }: { athleteId: string; onRemove: () => void }) {
  const athleteQ = useAthlete(athleteId, true)
  const programQ = useAthleteProgram(athleteId, true)
  const qc = useQueryClient()
  // Selected day + exercise ticks survive the tablet reloading the page (sleep,
  // redeploy). Kept per athlete for today only, so tomorrow starts fresh.
  const saved = loadGymState(athleteId)
  const [dayIndex, setDayIndex] = useState(saved.dayIndex)
  const [ticks, setTicks] = useState<Record<string, number[]>>(saved.ticks)

  const program = programQ.data?.program
  const weeks = program?.weeks ?? []
  // Single source of truth: the athlete's current week on their assignment.
  // Stepping the arrows sets it, so coach, gym view and athlete all agree.
  // (Unlocking is driven by started_at/weeks_unlocked, so this can't lock or
  // unlock anything for the athlete — it's purely "where they're up to".)
  const currentWeek = programQ.data?.currentWeek ?? 1
  const week = weeks.find((w) => w.week === currentWeek) ?? weeks[0]
  const day = week?.days[dayIndex]

  // Flag athletes who are overdue for VALD retesting.
  const lastTestQ = useLastTestDate(athleteId)
  const due = testingDue(lastTestQ.data)

  // Persisted completions so the coach can see what's actually been finished.
  const completionsQ = useDayCompletions(athleteId, program?.id)
  const toggleDayComplete = useToggleDayComplete()
  const savedDone = completionsQ.data?.done ?? new Set<string>()
  const dayDone = week ? savedDone.has(dayKey(week.week, dayIndex + 1)) : false

  const tickKey = `${week?.week ?? 0}-${dayIndex}`
  const done = new Set(ticks[tickKey] ?? [])
  const toggleTick = (i: number) =>
    setTicks((prev) => {
      const cur = new Set(prev[tickKey] ?? [])
      cur.has(i) ? cur.delete(i) : cur.add(i)
      return { ...prev, [tickKey]: [...cur] }
    })

  useEffect(() => {
    saveGymState(athleteId, { dayIndex, ticks })
  }, [athleteId, dayIndex, ticks])

  // A restored day may not exist in this week (e.g. week has fewer days).
  useEffect(() => {
    if (week && dayIndex >= week.days.length) setDayIndex(0)
  }, [week, dayIndex])

  const wIdx = Math.max(0, weeks.findIndex((w) => w.week === week?.week))
  type ProgramQData = { currentWeek: number } | null | undefined
  const setCurrentWeek = async (n: number) => {
    setDayIndex(0)
    const prev = currentWeek
    // Optimistic so the UI moves instantly…
    qc.setQueryData(['athlete-program', athleteId], (old: ProgramQData) =>
      old ? { ...old, currentWeek: n } : old
    )
    // …then persist. Must be awaited — supabase query builders are lazy and
    // never issue the request otherwise.
    const { error } = await supabase
      .from('athlete_programs')
      .update({ current_week: n })
      .eq('athlete_id', athleteId)
      .eq('active', true)
    if (error) {
      qc.setQueryData(['athlete-program', athleteId], (old: ProgramQData) =>
        old ? { ...old, currentWeek: prev } : old
      )
      console.error('Could not save current week:', error.message)
    }
  }
  const goToWeek = (i: number) => {
    if (i < 0 || i >= weeks.length) return
    setCurrentWeek(weeks[i].week)
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-border bg-surface-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-condensed text-base font-bold leading-none">
            {athleteQ.data?.name ?? 'Loading…'}
          </p>
          {week &&
            (weeks.length > 1 ? (
              <div className="mt-0.5 flex items-center gap-1">
                <button
                  onClick={() => goToWeek(wIdx - 1)}
                  disabled={wIdx <= 0}
                  className="flex h-5 w-5 items-center justify-center rounded text-gold disabled:opacity-25"
                  aria-label="Previous week"
                >
                  ‹
                </button>
                <span className="min-w-[52px] text-center font-condensed text-[11px] font-bold uppercase tracking-wide text-gold">
                  Week {week.week}
                </span>
                <button
                  onClick={() => goToWeek(wIdx + 1)}
                  disabled={wIdx >= weeks.length - 1}
                  className="flex h-5 w-5 items-center justify-center rounded text-gold disabled:opacity-25"
                  aria-label="Next week"
                >
                  ›
                </button>
              </div>
            ) : (
              <p className="truncate text-[11px] text-muted">Week {week.week}</p>
            ))}
        </div>
        {week && week.days.length > 1 && (
          <div className="flex gap-1">
            {week.days.map((d, i) => (
              <button
                key={i}
                onClick={() => setDayIndex(i)}
                className={cn(
                  'rounded-md px-2 py-0.5 font-condensed text-xs font-bold',
                  i === dayIndex
                    ? 'gold-gradient text-black'
                    : savedDone.has(dayKey(week.week, i + 1))
                    ? 'text-success'
                    : 'text-muted hover:text-gold'
                )}
              >
                {savedDone.has(dayKey(week.week, i + 1)) ? '✓' : ''}
                {dayLabel(d.label, i)}
              </button>
            ))}
          </div>
        )}
        <button onClick={onRemove} className="px-1 text-muted hover:text-danger" aria-label="Remove">
          ✕
        </button>
      </div>

      {lastTestQ.isSuccess && due.due && (
        <div className="flex-shrink-0 border-b border-gold/40 bg-gold/10 px-3 py-1.5 font-condensed text-[11px] font-bold uppercase tracking-[0.1em] text-gold">
          ⚠ Testing due · {due.neverTested ? 'never tested' : `${due.days}d since last test`}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {!program ? (
          <p className="p-4 text-center text-xs text-muted">
            {programQ.isLoading ? 'Loading…' : 'No program assigned'}
          </p>
        ) : (
          groupSupersets(day?.exercises ?? []).map((grp, gi) => {
            const rows = grp.items.map(({ ex, index: i }) => (
              <GymExercise
                key={`${week!.week}-${dayIndex}-${i}`}
                exercise={ex}
                athleteId={athleteId}
                programId={program.id}
                weekNum={week!.week}
                dayNum={dayIndex + 1}
                done={done.has(i)}
                onToggle={() => toggleTick(i)}
              />
            ))
            if (!grp.superset) return <div key={gi}>{rows}</div>
            return (
              <div key={gi} className="mb-1 rounded-lg border border-gold/40 bg-gold/[.06] py-1 pl-1.5 pr-1">
                <p className="px-1 pb-0.5 font-condensed text-[10px] font-bold uppercase tracking-[0.12em] text-gold">
                  ⇄ Superset {grp.letter} · back-to-back
                </p>
                {rows}
              </div>
            )
          })
        )}
      </div>

      {program && week && (
        <button
          onClick={() =>
            toggleDayComplete.mutate({
              athleteId,
              programId: program.id,
              week: week.week,
              day: dayIndex + 1,
              complete: !dayDone,
            })
          }
          disabled={toggleDayComplete.isPending}
          className={cn(
            'flex-shrink-0 border-t border-border py-2 font-condensed text-xs font-bold uppercase tracking-[0.15em] transition-colors',
            dayDone ? 'bg-success/15 text-success' : 'text-muted hover:text-gold'
          )}
        >
          {dayDone ? '✓ Day complete' : 'Mark day complete'}
        </button>
      )}
    </div>
  )
}

function GymExercise({
  exercise,
  athleteId,
  programId,
  weekNum,
  dayNum,
  done,
  onToggle,
}: {
  exercise: ProgramExercise
  athleteId: string
  programId: string
  weekNum: number
  dayNum: number
  done: boolean
  onToggle: () => void
}) {
  const { resolveId } = useExerciseLibrary()
  const [open, setOpen] = useState(false)
  const saveSet = useSaveSet()
  const exerciseId = resolveId(exercise)
  const history = useExerciseHistory(athleteId, exercise.name, open && !exercise.is_warmup)
  const setCount = exercise.sets ?? 0

  // Load + hold the weights already logged for this session so they persist on
  // screen (were previously write-only → looked like they vanished).
  const logged = useSessionSets(athleteId, exerciseId, programId, weekNum, dayNum, !exercise.is_warmup)
  const [vals, setVals] = useState<Record<number, string>>({})
  // Per-set save state, so a failed or unreadable entry is visible instead of
  // looking saved when it isn't.
  const [setState, setSetState] = useState<Record<number, 'error' | 'invalid' | undefined>>({})
  const markSet = (s: number, v: 'error' | 'invalid' | undefined) =>
    setSetState((prev) => ({ ...prev, [s]: v }))
  useEffect(() => {
    if (logged.data) setVals((prev) => ({ ...logged.data, ...prev }))
  }, [logged.data])

  const anyLogged = Object.values(vals).some((v) => v !== '' && v != null)

  const presc =
    exercise.sets && exercise.reps ? `${exercise.sets}×${exercise.reps}` : exercise.reps ?? ''

  if (exercise.is_warmup) {
    return (
      <div className="px-2 py-1.5 text-[11px] uppercase tracking-wide text-subtle">{exercise.name}</div>
    )
  }

  return (
    <div className={cn('mb-1 rounded-lg border', done ? 'border-success/40 bg-success/[.04]' : 'border-border bg-surface')}>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          onClick={onToggle}
          className={cn(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border text-[10px] font-bold',
            done ? 'border-success bg-success text-black' : 'border-border-strong text-transparent'
          )}
        >
          ✓
        </button>
        <button onClick={() => setOpen((v) => !v)} className="min-w-0 flex-1 text-left">
          <span className="block truncate font-condensed text-sm font-bold leading-tight">
            {exercise.group && <span className="mr-1 font-mono text-[10px] text-gold">{exercise.group}</span>}
            {exercise.name}
          </span>
          <span className="block truncate text-[11px] text-gold">
            {presc}
            {exercise.load ? ` · ${exercise.load}` : ''}
          </span>
          {anyLogged && (
            <span className="block truncate text-[11px] text-data">
              {Array.from({ length: setCount }, (_, s) => vals[s]).filter(Boolean).join(' · ')}kg
            </span>
          )}
        </button>
        {exercise.video && (
          <a
            href={exercise.video}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 rounded-full border border-danger/40 bg-danger/10 px-2 py-0.5 text-[11px] font-semibold text-danger"
            title="Watch demo"
          >
            ▶
          </a>
        )}
        {setCount > 0 && (
          <button onClick={() => setOpen((v) => !v)} className={cn('text-muted', open && 'rotate-180')}>
            ▾
          </button>
        )}
      </div>

      {open && setCount > 0 && (
        <div className="border-t border-border px-2 pb-2 pt-1.5">
          {history.data?.pr != null && (
            <p className="mb-1 text-[10px] uppercase tracking-wide text-gold">🏆 {history.data.pr}kg</p>
          )}
          <div className="flex flex-wrap gap-1">
            {Array.from({ length: setCount }).map((_, s) => (
              <input
                key={s}
                inputMode="decimal"
                value={vals[s] ?? ''}
                placeholder={history.data?.lastWeight != null ? String(history.data.lastWeight) : `S${s + 1}`}
                onChange={(e) => setVals((prev) => ({ ...prev, [s]: e.target.value }))}
                onBlur={(e) => {
                  if (!isRealAthleteId(athleteId)) return
                  const parsed = parseWeight(e.target.value)
                  if (!parsed.ok) return markSet(s, 'invalid')
                  if (parsed.value != null) setVals((prev) => ({ ...prev, [s]: String(parsed.value) }))
                  markSet(s, undefined)
                  saveSet.mutate(
                    {
                      athleteId,
                      programId,
                      exerciseId,
                      exerciseName: exercise.name,
                      week: weekNum,
                      day: dayNum,
                      setIndex: s,
                      weight: parsed.value,
                    },
                    { onError: () => markSet(s, 'error') }
                  )
                }}
                title={
                  setState[s] === 'invalid' ? 'Enter a number, e.g. 60 or 62.5'
                  : setState[s] === 'error' ? 'Not saved — check connection, then tap in and out to retry'
                  : undefined
                }
                className={cn(
                  'tabular h-8 w-11 rounded border bg-white/[.04] text-center font-condensed text-sm font-bold text-text outline-none placeholder:text-subtle/50 focus:border-gold',
                  setState[s] ? 'border-danger text-danger' : 'border-border-strong'
                )}
              />
            ))}
          </div>
          {Object.values(setState).some(Boolean) && (
            <p className="mt-1 text-[10px] text-danger">
              {Object.values(setState).includes('error')
                ? 'Not saved — check the connection, then tap the red box and away again.'
                : 'Enter numbers only, e.g. 60 or 62.5.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
